import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, normalizeStyle as _normalizeStyle, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, withCtx as _withCtx, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "bg-slateGray" }
const _hoisted_2 = { class: "text-card flex flex-col items-center justify-center gap-5 sm:gap-30px drop-shadow-section" }

import { ref, computed, onMounted, watch } from 'vue'
import type { Ref } from 'vue'
import SectionBase from '../general/SectionBase.vue'
import ResponsiveBackground from '../general/ResponsiveBackground.vue'
import hotspotData from '@/assets/data/mjp/hotspot-data.js'
import { MJPSection, MJPSectionData } from './types'
import HotspotContainer from '../general/HotspotContainer.vue'
import useLanguage from '@/composables/useLanguage'
import { useSettingsStore } from '@/store/settings'
import PopOver from '../general/PopOver.vue'
import SectionPage from '../general/SectionPage.vue'
import TextCard from '../general/TextCard.vue'
import BottomGradient from '../general/BottomGradient.vue'
import TitleComponent from '../general/TitleComponent.vue'
import SpotAnimationContainer from '../general/SpotAnimationContainer.vue'
import ResponsiveBackgroundVideo from '../general/ResponsiveBackgroundVideo.vue'
import useTabIndex from '@/composables/mjp/useTabIndex'
import BlackMatte from '../general/BlackMatte.vue'

interface Props {
  sectionData: MJPSectionData
  currentSection: MJPSection
  currentSectionHasLoaded: boolean
  currentPage: number
  forward: boolean
  activeHotspotId: string
  hoveredHotspotId: string
  prefersReducedMotion: boolean
  delayFades?: boolean
}


export default /*#__PURE__*/_defineComponent({
  __name: 'WaterTreatmentFacilitySection',
  props: {
    sectionData: null,
    currentSection: null,
    currentSectionHasLoaded: { type: Boolean },
    currentPage: null,
    forward: { type: Boolean },
    activeHotspotId: null,
    hoveredHotspotId: null,
    prefersReducedMotion: { type: Boolean },
    delayFades: { type: Boolean }
  },
  setup(__props: any) {

const props = __props as {
  sectionData: MJPSectionData
  currentSection: MJPSection
  currentSectionHasLoaded: boolean
  currentPage: number
  forward: boolean
  activeHotspotId: string
  hoveredHotspotId: string
  prefersReducedMotion: boolean
  delayFades?: boolean
}


const sectionBase: Ref<typeof SectionBase | null> = ref(null)
const background: Ref<typeof ResponsiveBackground | null> = ref(null)
const hotspotContainer: Ref<typeof HotspotContainer | null> = ref(null)
const text = useLanguage(props.currentSection)
const settingsStore = useSettingsStore()

// the following methods are repeated in each section,
// currently preferring template readability over DRY
const narrativeScrollProgress = computed(() => {
  return sectionBase.value ? sectionBase.value.state.narrativeScrollProgress : 0
})

const responsiveScrollOffset = computed(() => {
  return sectionBase.value
    ? sectionBase.value.state.responsiveScrollOffset
    : {
        proxyPosition: '',
        offsetX: 0,
      }
})

const sectionIsActive = computed(() => {
  return props.currentSection == props.sectionData.section
})

const pageRangeIsActive = (lo, hi) => {
  if (sectionIsActive.value && props.currentSectionHasLoaded) {
    return props.currentPage >= lo && props.currentPage <= hi
  } else {
    return false
  }
}

const opacityForPageRange = (lo, hi) => {
  return pageRangeIsActive(lo, hi) ? 1 : 0
}

const hotspotElement = (hotspotId) => {
  return hotspotContainer.value
    ? hotspotContainer.value.hotspotRefs[hotspotId].state.element
    : null
}

const sortedHotspots = computed(() => {
  return hotspotData[props.sectionData.section].sort((a, b) => {
    return a.left - b.left
  })
})

const focusCurrentTextCard = () => {
  ;(
    document.querySelector(
      `#${props.currentSection}-${props.currentPage + 1} .text-card`
    ) as HTMLElement
  )?.focus()
}

onMounted(() => {
  focusCurrentTextCard()
})

watch(
  () => props.currentPage,
  (currentValue, previousValue) => {
    focusCurrentTextCard()
  }
)

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(ResponsiveBackground, {
      id: `${props.sectionData.section}-bg`,
      ref_key: "background",
      ref: background,
      scrollOffset: _unref(responsiveScrollOffset),
      position: props.sectionData.focalPoint,
      altText: _unref(text).altText,
      delayFades: !props.prefersReducedMotion,
      dir: "mjp",
      sm: "waterTreatmentFacility-bg-sm.jpg",
      md: "waterTreatmentFacility-bg-md.jpg",
      lg: "waterTreatmentFacility-bg-lg.jpg",
      style: _normalizeStyle({
        opacity: opacityForPageRange(0, 99),
      })
    }, null, 8, ["id", "scrollOffset", "position", "altText", "delayFades", "style"]),
    _createVNode(ResponsiveBackgroundVideo, {
      manifest: "https://customer-vt66666ts449vzd9.cloudflarestream.com/515f264dd85dc0e2470d17c5220631e4/manifest/video.m3u8",
      sectionData: props.sectionData,
      scrollOffset: _unref(responsiveScrollOffset),
      currentSection: props.currentSection,
      currentSectionHasLoaded: props.currentSectionHasLoaded,
      currentPage: props.currentPage,
      minPage: 0,
      maxPage: 0,
      dir: "mjp"
    }, null, 8, ["sectionData", "scrollOffset", "currentSection", "currentSectionHasLoaded", "currentPage"]),
    _createVNode(ResponsiveBackground, {
      id: `${props.sectionData.section}-illo`,
      scrollOffset: _unref(responsiveScrollOffset),
      position: props.sectionData.focalPoint,
      altText: _unref(text).illoAltText,
      dir: "mjp",
      sm: "waterTreatmentFacility-illo-sm.png",
      md: "waterTreatmentFacility-illo-md.png",
      lg: "waterTreatmentFacility-illo-lg.png",
      style: _normalizeStyle({
        opacity: opacityForPageRange(1, 99),
      })
    }, null, 8, ["id", "scrollOffset", "position", "altText", "style"]),
    _createVNode(SpotAnimationContainer, {
      scrollProgress: _unref(narrativeScrollProgress),
      scrollOffset: _unref(responsiveScrollOffset),
      hotspots: _unref(hotspotData)[props.sectionData.section],
      activeHotspotId: props.activeHotspotId,
      hoveredHotspotId: props.hoveredHotspotId,
      position: props.sectionData.focalPoint,
      animationsVisible: 
        props.currentSection == props.sectionData.section &&
        props.currentPage >= 1
      ,
      transform: "",
      dir: "mjp"
    }, null, 8, ["scrollProgress", "scrollOffset", "hotspots", "activeHotspotId", "hoveredHotspotId", "position", "animationsVisible"]),
    (_unref(settingsStore).highContrast)
      ? (_openBlock(), _createBlock(BlackMatte, {
          key: 0,
          opacity: 0.2,
          style: _normalizeStyle({
        opacity: opacityForPageRange(0, 0),
      })
        }, null, 8, ["opacity", "style"]))
      : _createCommentVNode("", true),
    _createVNode(BottomGradient),
    _createVNode(SectionBase, {
      ref_key: "sectionBase",
      ref: sectionBase,
      thisSection: props.sectionData.section,
      currentSection: props.currentSection,
      currentSectionHasLoaded: props.currentSectionHasLoaded,
      currentPage: props.currentPage,
      activeHotspotId: props.activeHotspotId,
      forward: props.forward,
      hotspots: _unref(hotspotData)[props.sectionData.section],
      hotspotRefs: hotspotContainer.value ? hotspotContainer.value.hotspotRefs : {},
      position: props.sectionData.focalPoint
    }, {
      default: _withCtx(() => [
        _createVNode(SectionPage, {
          id: `${props.sectionData.section}-1`,
          class: "flex items-center justify-center"
        }, {
          default: _withCtx(() => [
            _createElementVNode("div", _hoisted_2, [
              _createVNode(TitleComponent, {
                text: _unref(text).title
              }, null, 8, ["text"]),
              _createVNode(TextCard, {
                text: _unref(text).waterCollected,
                enclosed: true,
                size: "sm",
                class: "md:mx-200px"
              }, null, 8, ["text"])
            ])
          ]),
          _: 1
        }, 8, ["id"]),
        _createVNode(SectionPage, {
          id: `${props.sectionData.section}-2`,
          class: "flex items-start justify-start"
        }, {
          default: _withCtx(() => [
            _createVNode(TextCard, {
              text: _unref(text).waterScrubbed,
              enclosed: true,
              size: "sm",
              class: "mt-24 md:mx-200px"
            }, null, 8, ["text"])
          ]),
          _: 1
        }, 8, ["id"]),
        _createVNode(SectionPage, {
          id: `${props.sectionData.section}-3`,
          class: "pointer-events-none"
        }, {
          default: _withCtx(() => [
            _createVNode(HotspotContainer, {
              ref_key: "hotspotContainer",
              ref: hotspotContainer,
              scrollProgress: _unref(narrativeScrollProgress),
              scrollOffset: _unref(responsiveScrollOffset),
              hotspots: _unref(sortedHotspots),
              activeHotspotId: props.activeHotspotId,
              hoveredHotspotId: props.hoveredHotspotId,
              position: props.sectionData.focalPoint,
              hotspotsVisible: pageRangeIsActive(2, 99),
              sectionText: _unref(text),
              transform: "",
              dir: "mjp"
            }, null, 8, ["scrollProgress", "scrollOffset", "hotspots", "activeHotspotId", "hoveredHotspotId", "position", "hotspotsVisible", "sectionText"]),
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(hotspotData)[props.sectionData.section], (hotspot, index) => {
              return (_openBlock(), _createBlock(PopOver, {
                key: index,
                index: index,
                source: 
            hotspotContainer.value
              ? hotspotContainer.value.hotspotRefs[hotspot.id].state.element
              : null
          ,
                data: hotspot.popover,
                dir: "mjp",
                text: _unref(text),
                isVisible: 
            props.activeHotspotId == hotspot.id && pageRangeIsActive(2, 99)
          ,
                offsetX: 0,
                offsetY: 0,
                baseTabIndex: _unref(useTabIndex)(HotspotContainer, index, PopOver, 10)
              }, null, 8, ["index", "source", "data", "text", "isVisible", "baseTabIndex"]))
            }), 128)),
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(hotspotData)[props.sectionData.section], (hotspot, index) => {
              return (_openBlock(), _createBlock(PopOver, {
                key: index,
                index: index,
                source: 
            hotspotContainer.value
              ? hotspotContainer.value.hotspotRefs[hotspot.id].state.element
              : null
          ,
                data: hotspot.preview,
                dir: "mjp",
                text: _unref(text),
                isVisible: 
            props.activeHotspotId.length == 0 &&
            props.hoveredHotspotId == hotspot.id &&
            pageRangeIsActive(2, 99)
          ,
                offsetX: 0,
                offsetY: 0,
                baseTabIndex: _unref(useTabIndex)(HotspotContainer, index, PopOver, 0)
              }, null, 8, ["index", "source", "data", "text", "isVisible", "baseTabIndex"]))
            }), 128))
          ]),
          _: 1
        }, 8, ["id"])
      ]),
      _: 1
    }, 8, ["thisSection", "currentSection", "currentSectionHasLoaded", "currentPage", "activeHotspotId", "forward", "hotspots", "hotspotRefs", "position"])
  ]))
}
}

})